/* eslint-disable no-unused-vars */
// @flow
import PropTypes from 'prop-types';
import type { Node } from 'react';
import type { CommonInputProps } from './InputBase';
import InputBase from './InputBase';
import type { ValidationProblem } from './types';

export type CompoundInputProps<ValueType, ApiType = any> = {
  children: Node | (ApiType => Node),
} & CommonInputProps<ValueType, ApiType>;
/**
 * An compound input requires child inputs to be able to edit its value - e.g.
 * Forms and RepeatingInputs
 */
export default class CompoundInput<
  CompoundValue,
  NameType,
  InputProps: CompoundInputProps<CompoundValue>,
> extends InputBase<CompoundValue, InputProps> {
  static childContextTypes = {
    parent: PropTypes.instanceOf(CompoundInput),
  };
  static +blankReceivedValue: CompoundValue;

  +inputs: Map<NameType, InputBase<any, any>> = new Map();

  getChildContext() {
    return { parent: this };
  }

  get currentValue(): CompoundValue {
    return super.currentValue;
  }
  get committedValue(): CompoundValue {
    return super.committedValue;
  }

  get validationProblems(): Array<ValidationProblem<any>> {
    return super.validationProblems.concat(
      ...(this.inputs ? Array.from(this.inputs.values()) : []).map(input =>
        this.addChildInputNamesToValidationProblems(input),
      ),
    );
  }

  set currentValue(value: CompoundValue) {
    if (this.currentValue === value) {
      if (typeof this.props.children === 'function') {
        this.forceUpdate();
      }
      return;
    }
    super.currentValue = value;
    this.forceUpdate(() => {
      for (const [name, childValue] of this.constructor.eachCompoundValue(super.currentValue)) {
        if (this.inputs) {
          const input = this.inputs.get(name);
          if (input && !input.areValuesEqual(input.currentValue, childValue)) {
            input.currentValue = childValue;
          }
        }
      }
    });
  }

  set committedValue(value: CompoundValue) {
    if (this.committedValue === value) {
      if (typeof this.props.children === 'function') {
        this.forceUpdate();
      }
      return;
    }
    super.committedValue = value;
    this.forceUpdate(() => {
      for (const [name, childValue] of this.constructor.eachCompoundValue(value)) {
        if (this.inputs) {
          const input = this.inputs.get(name);
          if (input && !input.areValuesEqual(input.committedValue, childValue)) {
            input.committedValue = childValue;
          }
        }
      }
    });
  }

  addChildInputNamesToValidationProblems(
    input: InputBase<any, any>,
  ): Array<ValidationProblem<any>> {
    return input.validationProblems.map(problem => ({
      ...problem,
      inputName: [input.name, ...problem.inputName],
    }));
  }
  canReceiveNewValue(value: CompoundValue) {
    return (
      super.canReceiveNewValue(value) ||
      Array.from(this.constructor.eachCompoundValue(value)).every(([name, childValue]) => {
        const input = this.inputs.get(name);
        if (input) {
          return input.canReceiveNewValue(childValue);
        }
        return true;
      })
    );
  }

  didReceiveNewValue(value: CompoundValue, bypassOverwriteCheck: boolean = false) {
    if (bypassOverwriteCheck || this.canReceiveNewValue(value)) {
      super.didReceiveNewValue(value, true);
      this.forceUpdate(() => {
        for (const [name, childValue] of this.constructor.eachCompoundValue(value)) {
          const input = this.inputs.get(name);
          if (input) {
            input.didReceiveNewValue(childValue, true);
          }
        }
      });
    }
  }

  // eslint-disable-next-line no-unused-vars,require-yield
  static *eachCompoundValue(value: CompoundValue): Iterator<[NameType, any]> {
    throw new Error('not implemented');
  }

  static setCompoundValueElement(
    compoundValue: CompoundValue,
    name: NameType,
    // eslint-disable-next-line no-unused-vars
    element: any,
  ): CompoundValue {
    throw new Error('not implemented');
  }

  static getCompoundValueElement(
    compoundValue: CompoundValue,
    // eslint-disable-next-line no-unused-vars
    name: NameType,
  ): any {
    throw new Error('not implemented');
  }

  getReceivedValueElement(name: NameType): any {
    return this.constructor.getCompoundValueElement(this.receivedValue, name);
  }

  getCurrentValueElement(name: NameType): any {
    return this.constructor.getCompoundValueElement(this.currentValue, name);
  }

  getCommittedValueElement(name: NameType): any {
    return this.constructor.getCompoundValueElement(this.committedValue, name);
  }

  registerInput(input: InputBase<any, any, any>) {
    this.inputs.set(input.name, input);
  }

  renameRegisteredInput(oldName: NameType, newName: NameType) {
    const input = this.inputs.get(oldName);
    if (!input) {
      throw new Error(`Cannot rename input with name ${String(oldName)} - it does not exist.`);
    }
    this.inputs.delete(oldName);
    this.inputs.set(newName, input);
  }

  unregisterInput(input: InputBase<any, any, any>) {
    if (this.inputs.get(input.name) === input) {
      this.inputs.delete(input.name);
    }
  }

  initializeValueFromChild(name: NameType, value: any) {
    const newValue = this.constructor.setCompoundValueElement(this.committedValue, name, value);
    this.committedValue = newValue;
  }

  childInputDidChange(name: NameType, value: any) {
    const newValue = this.constructor.setCompoundValueElement(this.currentValue, name, value);
    this.notifyInputDidChange(newValue);
    this.currentValue = newValue;
  }

  childInputDidCommitChange(name: NameType, value: any) {
    const newValue = this.constructor.setCompoundValueElement(this.committedValue, name, value);
    this.notifyInputDidCommitChange(newValue);
    this.committedValue = newValue;
    this.validate();
  }

  validate(batchingCallback: ?() => void): boolean {
    return super.validate(() => {
      if (typeof batchingCallback === 'function') {
        batchingCallback();
      }
      for (const input of this.inputs.values()) {
        input.validate();
      }
    });
  }

  hasValue(): boolean {
    const inputs = Array.from(this.inputs.values());
    if (inputs.length === 0) {
      return false;
    }
    return inputs.every(input => input.hasValue());
  }
}
